import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { AuthAPI } from '@certi/shared/api/auth/AuthAPI'
import type { Session, User } from '@/types/auth'
import { zustandStorage } from '@certi/shared/utils/storage'

type AuthState = {
  user?: User
  session?: Session
  accessToken?: string
  loading: boolean
  error?: string
  isHydrated: boolean

  // Actions
  signIn: (params: {
    email: string
    password: string
    remember?: boolean
  }) => Promise<void>
  signUp: (params: {
    firstName: string
    lastName: string
    email: string
    password: string
  }) => Promise<void>
  signOut: () => Promise<void>
  clearError: () => void

  // Internal
  _hasHydrated: boolean
  _setHasHydrated: (state: boolean) => void
}

const STORAGE_KEY = 'certi-auth-storage'

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: undefined,
      session: undefined,
      loading: false,
      error: undefined,
      isHydrated: false,
      _hasHydrated: false,

      signIn: async ({ email, password, remember: _remember = false }) => {
        set({ loading: true, error: undefined })
        try {
          const session = await AuthAPI.signIn(email, password)

          // Fetch user profile after successful login
          const user = await AuthAPI.getCurrentUser(session.accessToken)
          const sessionWithUser = { ...session, user }

          set({
            user,
            session: sessionWithUser,
            accessToken: session.accessToken,
            loading: false,
            error: undefined,
          })
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Sign in failed'
          set({
            loading: false,
            error: message,
            user: undefined,
            session: undefined,
            accessToken: undefined,
          })
          throw error
        }
      },

      signUp: async ({ firstName, lastName, email, password }) => {
        set({ loading: true, error: undefined })
        try {
          const session = await AuthAPI.signUp(firstName, lastName, email, password)

          // Fetch user profile after successful registration
          const user = await AuthAPI.getCurrentUser(session.accessToken)
          const sessionWithUser = { ...session, user }

          set({
            user,
            session: sessionWithUser,
            accessToken: session.accessToken,
            loading: false,
            error: undefined,
          })
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Registration failed'
          set({
            loading: false,
            error: message,
            user: undefined,
            session: undefined,
            accessToken: undefined,
          })
          throw error
        }
      },

      signOut: async () => {
        set({ loading: true })
        try {
          const { session } = get()
          if (session?.refreshToken) {
            await AuthAPI.signOut(session.refreshToken)
          }

          set({
            user: undefined,
            session: undefined,
            accessToken: undefined,
            loading: false,
            error: undefined,
          })
        } catch (error) {
          set({ loading: false })
          // Even if sign out fails on server, clear local data
          set({
            user: undefined,
            session: undefined,
            accessToken: undefined,
            error: undefined,
          })
        }
      },

      clearError: () => {
        set({ error: undefined })
      },

      _setHasHydrated: (state: boolean) => {
        set({ _hasHydrated: state, isHydrated: state })
      },
    }),
    {
      name: STORAGE_KEY,
      storage: zustandStorage,
      onRehydrateStorage: () => (state) => {
        state?._setHasHydrated(true)
      },
      partialize: (state) => ({
        user: state.user,
        session: state.session,
      }),
    }
  )
)

// Auto-refresh token logic
let refreshTimer: NodeJS.Timeout | null = null

const setupTokenRefresh = () => {
  const state = useAuthStore.getState()
  
  if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }

  if (!state.session?.accessToken || !state.session?.refreshToken) {
    return
  }

  // Check if token is expired or will expire soon (5 minutes buffer)
  const isExpired = AuthAPI.isTokenExpired(state.session.accessToken)
  const expiresIn = state.session.expiresIn
  const refreshTime = isExpired ? 0 : Math.max(0, (expiresIn - 300) * 1000) // 5 minutes before expiry

  refreshTimer = setTimeout(async () => {
    try {
      const currentState = useAuthStore.getState()
      if (!currentState.session?.refreshToken) return

      const newSession = await AuthAPI.refreshToken(currentState.session.refreshToken)
      const sessionWithUser = { ...newSession, user: currentState.user }

      useAuthStore.setState({
        session: sessionWithUser,
      })

      // Setup next refresh
      setupTokenRefresh()
    } catch (error) {
      console.error('Token refresh failed:', error)
      // Sign out user if refresh fails
      useAuthStore.getState().signOut()
    }
  }, refreshTime)
}

// Subscribe to auth state changes to setup token refresh
useAuthStore.subscribe((state) => {
  if (state.session?.accessToken) {
    setupTokenRefresh()
  } else if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }
})